import { GuiaDTO } from "./guia";
import { DireccionDTO } from "./direccion";


/**
 * Status del viaje de EAD. El proceso es el siguiente. El numero es el status del proceso:
 * 0.- Primero se crea el viaje, el viaje aún no se considera en trayecto
 * 1.- El chofer inicia el viaje en la app móvil
 * 2.- En la parte web se le da inicio al viaje despues de que el chofer lo inició en la app móvil
 * 3.- Despues de entregar las guias y acuses el chofer le da finalizar viaje en el móvil.
 * 4.- El personal de la sucursal le da finalizar viaje desde la parte web para poder iniciar con la descarga. Se descarga el camión de EAD
 * 5.- Al terminar la descarga de EAD al usuario se le sugiere marcarlo como PendienteVerificación, que implica que se van a mostrar las guias entregadas y a seleccionar los acuses entregados
 * 6.- Despues de la verificación se cierra el viaje
 */
export enum StatusViajeEAD {
    /**Ya se le asignó un destino y un chofer a ese camión, pero que el camión aún no esta en camino */
    Creado = 0,

    /**El viaje ya fue iniciado en la app móvil por un chofer pero aún no esta en trayecto */
    IniciadoApp = 1,

    /**El camión ya salió */
    EnTrayecto = 2,

    /**El usuario ya finalizó el viaje desde la app móvil, pero aún esta pendiente de descargar */
    FinalizadoAppMovil = 3,

    /**
     * El usuario en la parte web marca el viaje como finalizado para que pueda iniciar su descarga. Este status indica que el camión ya regreso a la sucursal. Este status
     * afecta al status de las guias ya que aqui se les asignada de nuevo el IdSucursal
     */
    FinalizadoPendienteDescarga = 4,

    /**
     * El camión ya fue descargado por completo pero el viaje aún no ha sido verificado por el personal administrativo, la verficación implica revisar las guias que fueron entregadas, las que fueron
     * rechazadas y las de entregas incompletas, y mostrar el total pagado. Aqui tambien se seleccionan los acuses que si fueron firmados por el cliente
     */
    PendienteVerificacion = 5,

    /**El viaje ya volvió a la sucursal */
    Finalizado = 6,

    /**El viaje fue cancelado antes de ser iniciado.  */
    Cancelado = 7,

    /**El viaje fue cancelado despues de ser iniciado */
    CanceladoEnTrayecto = 8
}

/**Un viaje de entrega a domicilio */
export interface ViajeEAD {
    IdRegistro: number;

    Folio: number;

    /**Usuario que creó el viaje */
    IdUsuario: number;

    /**Id sucursal de salida del viaje */
    IdSucursal: number;

    /**Fecha real de creación del viaje */
    FechaCreacion: Date | string;

    /**
     * Fecha de inicio del viaje. Un CHECK verifica que no sea null si FechaLlegada!= null.
     * Al estar asignada esta fecha el status se considera como EnTrayecto
     */
    FechaInicio: Date | string | null;

    /**Fecha en la que el usuario finalizó el viaje en la app móvil. Al estar asignada esta fecha el status se considera como FinalizoAppMovil */
    FechaFinalizoAppMovil: Date | string | null;

    /**
     * Fecha en la que el usuario de la parte web le da finalización al viaje despues de que el usuario de la parte móvil lo hace.
     * Al estar asignada esta fecha el status es FechaFinalizoPendienteDescarga. 
     */
    FechaLlegadaPendienteDescarga: Date | string | null;

    /**Fecha en la que el camión ya fue descargado por completo. Al estar asignada esta fecha el status del viaje es PendienteVerificacion */
    FechaPendienteVerificacion: Date | string | null;

    /**Fecha en la que el proceso de verificación y de selección de los acuses terminó. Este es el ultimo paso del proceso asi qu el status se considera Finalizado */
    FechaFinalizo: Date | string | null;

    /**Fecha en la que fue cancelado el viaje, si es que fue cancelado */
    FechaCancelacion: Date | string | null;

    /**Id de la ruta de este viaje */
    IdRutaEntregaDomicilio: number;

    /**Chofer asignado a este viaje */
    IdChoferEAD: number;

    /**Camión de EAD */
    IdCamionEAD: number;
}

export interface ViajeEADDto extends ViajeEAD {
    /**Id del usuario que lo inició en la App */
    IdUsuarioApp: number | null;
    /**Nombre del usuario que lo inició en la app móvil */
    NombreUsuarioApp: string;
    Status: StatusViajeEAD;
    NombreSucursal: string;
    NombreUsuario: string;
    NombreRuta: string;
    NombreChofer: string;
    NombreCamion: string;
    TieneXmlCartaPorteTimbrado: boolean;
    TienexmlImpreso: boolean;
    ClientesEspecialesCartaPorte: ClienteDatosCP[];
}

export interface ClienteDatosCP
{
    Idcliente: number| null;
    NombreCliente: string;
    IdViajeEad: number;
    Folio: string;
}

/**Datos del filtro de la consulta de los viajes de EAD */
export interface FiltroViajeEAD {
    Status?: StatusViajeEAD | null;
    IdChofer?: number | null;
    IdCamion?: number | null;
    IdSucursal?: number | null;
    IdRuta?: number | null;
    Folio?: number | null;
    FechaCreacionInicio?: Date | string | null;
    FechaCreacionFinal?: Date | string | null;
}

/**Modelo para crear un viaje de EAD */
export interface NuevoViajeEADModel {
    IdChofer: number;
    IdCamion: number;
}

/**Contiene los datos que se le mostrarán al usuario para que este verifique un viaje de EAD finalizado */
export interface VerificacionViajeEADDto {
    /**Importe total pagado */
    ImporteTotal: number;

    /**Parte del importe total que no es el de crédito */
    ImporteContado: number;

    /**Parte del importe total que se pago en efectivo */
    ImporteEfectivo: number;

    /**Parte del importe total que se pago en cheque */
    ImporteCheque: number;

    /**Parte del importe que se paga de crédito */
    ImporteCredito: number;

    /**Guias que se entregaron y que tienen acuse ya sea interno o externo. Estos son los que debe de entregar el chofer */
    AcusesFirmados: GuiaDTO[];
    /**Guias que fueron entregadas en este viaje */
    GuiasEntregadas: GuiaDTO[];
    /**Guias que el cliente rechazó explicitamente */
    GuiasRechazadas: GuiaRechazadaDto[];
    /**Guias de las entregas en las que no se encontró al cliente */
    GuiasEntregaIncompleta: GuiaDTO[];

    /**Listado de las entregas donde no se pudo localizar al cliente */
    EntregasIncompletas: EntregaIncompletaDomicilioDTO[];
}


/**Indica que no se pudo contactar con el destino de una entrega a domicilo */
export interface EntregaIncompletaDomicilio {

    /**Clave unica generada en el lado del cliente para permitir la sincronización cliente/servidor */
    ClaveUnica: string;

    /**Viaje en el que se intentó realizar esta entrega */
    IdViajeEAD: number;
    ViajeEAD: ViajeEAD;

    /**Fecha en la que se intentó realizar la entrega segun la app móvil */
    FechaReal: Date | string;

    /**Motivo de la entrega incompleta */
    Motivo: string;

    /**Direccion de destino que fue visitada */
    IdDireccionCliente: number;

}

export interface EntregaIncompletaDomicilioDTO extends EntregaIncompletaDomicilio {
    NombreCliente: string;
    Direccion: DireccionDTO;
}

export interface GuiaRechazadaDto {
    IdRegistro: number;
    Folio: string;

    IdClienteOrigen: number;
    NombreClienteOrigen: string;

    IdSucursalOrigen: number;
    NombreSucursalOrigen: string;

    IdClienteDestino: number;
    NombreClienteDestino: string;

    IdSucursalDestino: number;
    NombreSucursalDestino: string;

    MotivoRechazo: string;
}


// WEBPACK FOOTER //
// ./app/models/viajeEAD.ts